import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FunctionComponent,
  type ReactNode,
} from 'react';
import { useIsWithinBreakpoints, useIsWithinMaxBreakpoint } from '@elastic/eui';

type Drawer = 'nav' | 'filter' | null;

interface UiChromeContextValue {
  /** Which overlay drawer is currently open (or `null`). */
  openDrawer: Drawer;
  isNavOpen: boolean;
  isFilterOpen: boolean;
  openNav: () => void;
  closeNav: () => void;
  toggleNav: () => void;
  openFilter: () => void;
  closeFilter: () => void;
  toggleFilter: () => void;
  /**
   * The SINGLE chrome breakpoint read, computed once here and consumed from
   * context by BurgerNav, TopBar and CampaignFilters. Keeps
   * `useIsWithinBreakpoints` from being scattered across the chrome subtree.
   */
  /** l/xl: nav is push-docked; top-bar actions render inline. */
  isDocked: boolean;
  /** xs/s/m: nav + drawers overlay; top-bar actions collapse to a menu. */
  isOverlay: boolean;
  /** xs: smallest viewport — brand text hides, filter flyout goes full-width. */
  isPhone: boolean;
  /**
   * Whether the top-bar/toolbar actions collapse into a "⋯" overflow menu.
   * Decoupled from nav docking: triggers only below `l` (≤991px), since we'll
   * never have enough actions to crowd a 992px+ toolbar. Defaults to inline
   * (false) pre-mount so desktop never flashes the overflow button.
   */
  isActionsOverflow: boolean;
}

const UiChromeContext = createContext<UiChromeContextValue | null>(null);

/**
 * Owns which chrome drawer is open and enforces the two-drawer rule:
 * on overlay breakpoints, opening one drawer closes the other. Because only a
 * single `openDrawer` value can be set at a time, the rule is structural.
 *
 * On docked (desktop) breakpoints the BurgerNav is push-docked and is NOT
 * tracked by this state — only the FilterFlyout overlays — so docking and the
 * flyout never conflict.
 */
export const UiChromeProvider: FunctionComponent<{ children: ReactNode }> = ({
  children,
}) => {
  const [openDrawer, setOpenDrawer] = useState<Drawer>(null);

  // The one chrome breakpoint read (was previously local to BurgerNav).
  // Docked (push) on large screens; overlay below; phone = smallest.
  //
  // `useIsWithinBreakpoints` is window-only: SSR and the first client render
  // would otherwise disagree and `EuiCollapsibleNav`'s docked flyout (portal +
  // focus trap) hydrates mismatched. So render a deterministic non-docked
  // baseline on the server + first client paint, then switch to the real
  // breakpoint after mount. Hooks stay called unconditionally; only the values
  // are gated.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const isDockedRaw = useIsWithinBreakpoints(['xl']);
  const isPhoneRaw = useIsWithinBreakpoints(['xs']);
  // Actions collapse only below `l` (≤991px) — explicit, and independent of the
  // nav docking breakpoint.
  const isActionsOverflowRaw = useIsWithinMaxBreakpoint('m');
  const isDocked = mounted && isDockedRaw;
  const isPhone = mounted && isPhoneRaw;
  const isActionsOverflow = mounted && isActionsOverflowRaw;

  const value = useMemo<UiChromeContextValue>(
    () => ({
      openDrawer,
      isNavOpen: openDrawer === 'nav',
      isFilterOpen: openDrawer === 'filter',
      openNav: () => setOpenDrawer('nav'),
      closeNav: () => setOpenDrawer(prev => (prev === 'nav' ? null : prev)),
      toggleNav: () => setOpenDrawer(prev => (prev === 'nav' ? null : 'nav')),
      openFilter: () => setOpenDrawer('filter'),
      closeFilter: () =>
        setOpenDrawer(prev => (prev === 'filter' ? null : prev)),
      toggleFilter: () =>
        setOpenDrawer(prev => (prev === 'filter' ? null : 'filter')),
      isDocked,
      isOverlay: !isDocked,
      isPhone,
      isActionsOverflow,
    }),
    [openDrawer, isDocked, isPhone, isActionsOverflow]
  );

  return (
    <UiChromeContext.Provider value={value}>
      {children}
    </UiChromeContext.Provider>
  );
};

export function useUiChrome(): UiChromeContextValue {
  const ctx = useContext(UiChromeContext);
  if (!ctx) {
    throw new Error('useUiChrome must be used within a UiChromeProvider');
  }
  return ctx;
}
